import React, { useState } from 'react';
import { faEnvelope, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { Table, Placeholder, Form, Button } from 'react-bootstrap-5';
import EmptyState from '../../empty_state';
import MailersTemplatesTableRow from './mailers_templates_table_row';
import Paginator from '../../paginator';
import type { MailersTemplate } from 'bundles/connect/types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CreateTemplateModal from './create_template_modal';

const NUM_PER_PAGE = 15;

type MailersTemplatesTableProps = {
  callListId: number,
  currentPage: number;
  loading: boolean;
  selectedTemplates: number[];
  setSelectedTemplates: React.Dispatch<React.SetStateAction<number[]>>;
  setTemplates: React.Dispatch<React.SetStateAction<MailersTemplate[]>>;
  templates: MailersTemplate[];
  total: number;
  onChangePage: (page: number) => void
}

export default function MailersTemplatesTable({
  callListId,
  currentPage,
  loading,
  selectedTemplates,
  setSelectedTemplates,
  setTemplates,
  templates,
  total,
  onChangePage
}: MailersTemplatesTableProps) {
  const [showCreateModal, setShowCreateModal] = useState(false);

  const handleSelectAll = () => {
    if (selectedTemplates.length === templates.length) {
      return setSelectedTemplates([]);
    } else {
      setSelectedTemplates(templates.map(t => t.id));
    }
  };

  const emptyState = !loading && templates.length === 0;

  return (
    <>
      {!emptyState && (
        <div className="table-responsive overflow-auto position-relative bg-white z-2 h-100">
          <Table borderless className="sticky-table w-100">
            <thead className="bg-light">
              <tr className="border-bottom sticky-heading">
                <th className="py-3 px-4 align-middle" style={{ width: 36 }}>
                  <Form.Check
                    checked={selectedTemplates.length === templates.length && templates.length > 0}
                    type="checkbox"
                    onChange={handleSelectAll}
                  />
                </th>
                <th className="sticky-cell border-end-0 py-3 px-4 fw-bolder">
                  Name
                </th>
                <th className="py-3 px-4 fw-bolder" style={{ width: 120 }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="border-0" id="mailers_templates">
              {loading ? (
                Array.from({ length: 15 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 3 }).map((_, j) => (
                      <td className="py-3 px-4" key={j}>
                        <Placeholder animation="wave">
                          <div>
                            <Placeholder as="div" className="rounded" xs={12} />
                          </div>
                        </Placeholder>
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                templates.map(template => (
                  <MailersTemplatesTableRow
                    callListId={callListId}
                    key={template.id}
                    selectedTemplates={selectedTemplates}
                    setSelectedTemplates={setSelectedTemplates}
                    setTemplates={setTemplates}
                    template={template}
                  />
                ))
              )}
            </tbody>
          </Table>
        </div>
      )}
      {total > NUM_PER_PAGE && (
        <div
          className="px-3 py-4 d-flex justify-content-end position-sticky z-3 border-top bottom-0 bg-white"
          style={{ minHeight: 82 }}
        >
          <Paginator currentPage={currentPage} perPage={NUM_PER_PAGE} total={total} onSelect={onChangePage} />
        </div>
      )}
      {emptyState &&
        <div className="d-flex flex-column justify-content-center align-items-center w-100 h-100">
          <EmptyState icon={faEnvelope}
            iconSize="2x"
            text="Use the New Template button to begin creating your first mailer template"
            title="Let's Get Started"
          />
          <Button className="text-nowrap" variant="secondary" onClick={() => setShowCreateModal(true)}>
            <FontAwesomeIcon icon={faPlus} />
            New Template
          </Button>
          <CreateTemplateModal
            callListId={callListId}
            show={showCreateModal}
            onClose={() => setShowCreateModal(false)}
          />
        </div>
      }
    </>
  );
}
